/**
 * Utility functions for handling category and subcategory key matching
 * Supports both formats: "property-for-sale" and "property-for-sale-7"
 */

/**
 * Check if a subcategoryKey matches a specific category type
 * @param {string} subcategoryKey - The key to check (e.g., "property-for-sale-7" or "property-for-sale")
 * @param {string} categoryType - The category type to match (e.g., "property-for-sale")
 * @returns {boolean} - True if the key matches the category type
 */
export const isCategoryType = (subcategoryKey, categoryType) => {
  if (!subcategoryKey || !categoryType) return false;
  
  // Handle both formats: "property-for-sale" and "property-for-sale-7"
  return subcategoryKey.includes(categoryType) || subcategoryKey === categoryType;
};

/**
 * Check if a subcategoryKey is a property category (sale or rent)
 * @param {string} subcategoryKey - The key to check
 * @returns {boolean} - True if it's a property category
 */
export const isPropertyCategory = (subcategoryKey) => {
  return isCategoryType(subcategoryKey, 'property-for-sale') || 
         isCategoryType(subcategoryKey, 'property-for-rent');
};

/**
 * Check if a subcategoryKey is a motors category
 * @param {string} subcategoryKey - The key to check
 * @returns {boolean} - True if it's a motors category
 */
export const isMotorsCategory = (subcategoryKey) => {
  return isCategoryType(subcategoryKey, 'motors');
};

/**
 * Check if a subcategoryKey is a jobs category
 * @param {string} subcategoryKey - The key to check
 * @returns {boolean} - True if it's a jobs category
 */
export const isJobsCategory = (subcategoryKey) => {
  return isCategoryType(subcategoryKey, 'jobs');
};

/**
 * Check if a subcategoryKey is a classified category
 * @param {string} subcategoryKey - The key to check
 * @returns {boolean} - True if it's a classified category
 */
export const isClassifiedCategory = (subcategoryKey) => {
  return isCategoryType(subcategoryKey, 'classified');
};

/**
 * Check if a subcategoryKey is a community category
 * @param {string} subcategoryKey - The key to check
 * @returns {boolean} - True if it's a community category
 */
export const isCommunityCategory = (subcategoryKey) => {
  return isCategoryType(subcategoryKey, 'community');
};

/**
 * Extract the base category from a subcategoryKey
 * @param {string} subcategoryKey - The key to parse (e.g., "property-for-sale-7")
 * @returns {string|null} - The base category (e.g., "property-for-sale") or null
 */
export const getBaseCategoryFromKey = (subcategoryKey) => {
  if (!subcategoryKey) return null;
  
  // If it contains a dash and number at the end, extract the base
  const parts = subcategoryKey.split('-');
  if (parts.length > 1 && !isNaN(parts[parts.length - 1])) {
    // Remove the last part (the ID) and rejoin
    return parts.slice(0, -1).join('-');
  }
  
  // Otherwise, return as is
  return subcategoryKey;
};

/**
 * Check if subcategoryKey should show specific features
 * @param {string} subcategoryKey - The key to check
 * @param {Array<string>} allowedCategories - Array of allowed category types
 * @returns {boolean} - True if the key matches any allowed category
 */
export const shouldShowForCategories = (subcategoryKey, allowedCategories) => {
  if (!subcategoryKey || !Array.isArray(allowedCategories)) return false;
  
  return allowedCategories.some(category => isCategoryType(subcategoryKey, category));
};